import React, { useMemo, useState } from 'react';

import { Popover, Stack } from '@mui/material';
import { DateRangeCalendar, LocalizationProvider, MultiInputDateRangeField } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { endOfDay, format, isAfter, isValid, startOfDay } from 'date-fns';
import styled, { css } from 'styled-components';

import { CaretDown } from 'src/app/components/icons';
import { Button, LinkButton } from 'src/app/components/lib/button';
import { useDarkMode } from 'src/app/state/dark-mode';

import type { DateRange } from '@mui/x-date-pickers-pro';
import type { NonEmptyDateRange } from '@mui/x-date-pickers-pro/internals/models';

const FormatString = 'MM/dd/yyyy';

export type DatePickerRangeProps = {
  buttonLabel?: string;
  className?: string;
  disabled?: boolean;
  disableFooter?: boolean;
  onDateChange: (dateChange: NonEmptyDateRange<Date> | [null, null]) => void;
  shouldDisableDate?: (day: unknown, position: 'end' | 'start') => boolean;
  value?: NonEmptyDateRange<Date>;
};

export function DateRangePicker({
  onDateChange,
  value,
  shouldDisableDate,
  disabled = false,
  className,
  disableFooter = false,
  buttonLabel,
}: DatePickerRangeProps) {
  const darkMode = useDarkMode();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [dateRange, setDateRange] = useState<DateRange<Date>>(value ?? [null, null]);
  const [inputDateRange, setInputDateRange] = useState<DateRange<Date>>(value ?? [null, null]);

  const open = Boolean(anchorEl);
  const popoverId = 'date-range';

  const formatDateRange = (range: DateRange<Date>) => {
    const [start, end] = range;
    if (!start || !end) {
      return 'Select date range';
    }
    // We can safely assert these as Date since we've checked for null
    return `${format(start, FormatString)} - ${format(end, FormatString)}`;
  };

  const getButtonLabel = useMemo(() => {
    if (buttonLabel && !dateRange[0] && !dateRange[1]) {
      return buttonLabel;
    }
    return formatDateRange(dateRange);
  }, [buttonLabel, dateRange]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (dateRange[0] && dateRange[1]) {
      onDateChange(dateRange as NonEmptyDateRange<Date>);
    }
  };

  const handleClearDates = () => {
    const clearedRange: DateRange<Date> = [null, null];
    setDateRange(clearedRange);
    setInputDateRange(clearedRange);
    onDateChange([null, null]);
  };

  const onDateRangeChange = (range: DateRange<Date>) => {
    const startDate = range[0] ? startOfDay(range[0]) : null;
    const endDate = range[1] ? endOfDay(range[1]) : null;

    const cleanedRange: DateRange<Date> = [startDate, endDate];
    setDateRange(cleanedRange);
    setInputDateRange(cleanedRange);
  };

  const onInputDateRangeChange = (range: DateRange<Date>) => {
    if (!range[0] || !range[1]) {
      return;
    }

    const cleanedRange: DateRange<Date> = [startOfDay(range[0]), endOfDay(range[1])];
    setInputDateRange(cleanedRange);
    if (
      isValid(cleanedRange[0]) &&
      isValid(cleanedRange[1]) &&
      isAfter(cleanedRange[1] ?? new Date(), cleanedRange[0] ?? new Date())
    ) {
      setDateRange(cleanedRange);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledButton
        aria-controls='actions-menu'
        aria-describedby={popoverId}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup='true'
        automationId='date-range-picker-button'
        className={className}
        disabled={disabled}
        endIcon={<CaretDown fill={darkMode ? 'var(--color-brand-primary-white)' : undefined} />}
        id='date-range-picker-button'
        label={getButtonLabel}
        variant='white'
        onClick={handleClick}
      />
      <StyledPopover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        id={popoverId}
        open={open}
        onClose={handleClose}
      >
        <StyledStack $disableFooter={disableFooter} direction='column'>
          <StyledMultiInputDateRangeField
            slots={{ separator: React.Fragment }}
            value={inputDateRange}
            onChange={(newValue) => onInputDateRangeChange(newValue as DateRange<Date>)}
          />
          <StyledDateRangeCalendar
            $disableFooter={disableFooter}
            shouldDisableDate={shouldDisableDate}
            value={dateRange}
            onChange={(newValue) => onDateRangeChange(newValue as DateRange<Date>)}
          />
          {!disableFooter && (
            <DateRangePickerFooter>
              <LinkButton
                automationId='date-range-picker-clear-dates'
                buttonSize='small'
                label='Clear dates'
                onClick={handleClearDates}
              />
            </DateRangePickerFooter>
          )}
        </StyledStack>
      </StyledPopover>
    </LocalizationProvider>
  );
}

const StyledDateRangeCalendar = styled(DateRangeCalendar)<{ $disableFooter?: boolean }>`
  .MuiDateRangeCalendar-monthContainer {
    border-right: none !important;
  }
  .MuiPickersArrowSwitcher-root,
  .MuiPickersArrowSwitcher-button {
    padding-top: 0px;
    padding-bottom: 0px;
  }
  .Mui-selected,
  .MuiDateRangePickerDay-dayInsideRangeInterval {
    font: var(--font-regular-14pt-semibold);
  }
  .MuiDayCalendar-slideTransition {
    min-height: 202px;
  }

  ${({ $disableFooter }) =>
    !$disableFooter
      ? css`
          margin: 12px 0 12px;
        `
      : css`
          margin-top: 16px;
        `}
`;

const StyledMultiInputDateRangeField = styled(MultiInputDateRangeField)`
  margin: 0px 24px;
  .MuiInputBase-input {
    font: var(--font-regular-14pt-normal);
    padding: var(--sizes-30) 10px var(--sizes-30) var(--sizes-40);
  }
`;

const StyledStack = styled(Stack)<{ $disableFooter?: boolean }>`
  ${({ $disableFooter }) =>
    !$disableFooter
      ? css`
          margin-top: 16px;
        `
      : css`
          margin: 16px 0px;
        `}
`;

const StyledPopover = styled(Popover)`
  margin-top: 8px;
`;

const StyledButton = styled(Button)`
  outline: 1px solid var(--color-gray-60);
`;

export const DateRangePickerFooter = styled.div`
  border-top: 1px solid var(--color-gray-20);
  height: 42px;
  display: flex;
  gap: var(--sizes-70);
  padding: 0 var(--sizes-50);
  align-items: center;
  justify-content: flex-start;

  > button {
    padding: 0;
    min-width: auto;
  }
`;
